'use client';

import React, { useState, useRef, useEffect } from 'react';

const benefits = [
  {
    icon: 'M9 20l-5.447-2.724A1 1 0 0 1 3 16.382V5.618a1 1 0 0 1 1.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0 0 21 18.382V7.618a1 1 0 0 0-.553-.894L15 4m0 13V4m0 0L9 7',
    title: 'Real-Time Shipment Tracking',
    desc: 'Monitor every container, air freight, and inland delivery from pickup to final delivery \u2014 live, 24/7.',
  },
  {
    icon: 'M9 12h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2z',
    title: 'Documents & Statements',
    desc: 'Access bills of lading, commercial invoices, customs declarations, and account statements in one place.',
  },
  {
    icon: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4',
    title: 'Freight Booking',
    desc: 'Create and manage sea, air, and inland freight bookings directly \u2014 no phone calls, no waiting.',
  },
  {
    icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0 1 18 14.158V11a6.002 6.002 0 0 0-4-5.659V5a2 2 0 0 0-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 1 1-6 0v-1m6 0H9',
    title: 'Instant Notifications',
    desc: 'Get alerts on shipment milestones, customs clearance, and delivery confirmations automatically.',
  },
  {
    icon: 'M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2z',
    title: 'Quotes & Rate Requests',
    desc: 'Request freight quotes and compare rates for your routes \u2014 all within your portal account.',
  },
  {
    icon: 'M17 20h5v-2a3 3 0 0 0-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 0 1 5.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 0 1 9.288 0M15 7a3 3 0 1 1-6 0 3 3 0 0 1 6 0z',
    title: 'Dedicated Account View',
    desc: 'See your full shipping history, active shipments, and account balance \u2014 all in one dashboard.',
  },
];

interface FormData {
  fullName: string;
  company: string;
  email: string;
  phone: string;
  country: string;
  shipmentType: string;
}

const MyWTOPortalSection: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formStartedAt] = useState(() => Date.now());
  const [form, setForm] = useState<FormData>({
    fullName: '',
    company: '',
    email: '',
    phone: '',
    country: '',
    shipmentType: '',
  });

  // Animated portal stats
  const portalStatsRef = useRef<HTMLDivElement>(null);
  const [portalStatsStarted, setPortalStatsStarted] = useState(false);
  const portalStats = [
    { value: 1284, label: 'Shipments Tracked' },
    { value: 347, label: 'Documents Accessed' },
    { value: 98, label: 'On-Time Delivery %' },
  ];
  const [portalCounts, setPortalCounts] = useState(portalStats.map(() => 0));

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !portalStatsStarted) {
          setPortalStatsStarted(true);
        }
      },
      { threshold: 0.3 }
    );
    if (portalStatsRef.current) observer.observe(portalStatsRef.current);
    return () => observer.disconnect();
  }, [portalStatsStarted]);

  useEffect(() => {
    if (!portalStatsStarted) return;
    portalStats.forEach((stat, i) => {
      const duration = 1600;
      const steps = 60;
      const increment = stat.value / steps;
      let step = 0;
      const timer = setInterval(() => {
        step++;
        const current = Math.min(Math.round(increment * step), stat.value);
        setPortalCounts((prev) => {
          const next = [...prev];
          next[i] = current;
          return next;
        });
        if (step >= steps) clearInterval(timer);
      }, duration / steps);
    });
  }, [portalStatsStarted]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/mail.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          formType: 'portal-access',
          website: '',
          formStartedAt,
        }),
      });
      const responseText = await res.text();
      let data: { error?: string } = {};
      try {
        data = responseText ? JSON.parse(responseText) : {};
      } catch {
        throw new Error(responseText || 'Empty response from mail server.');
      }
      if (!res.ok) {
        throw new Error(data.error || responseText || 'Submission failed. Please try again.');
      }
      setSubmitted(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="my-wto-portal"
      className="relative overflow-hidden"
      style={{ background: '#1A3558' }}
    >
      {/* Decorative ambient blobs */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[400px] pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at center, rgba(27,107,138,0.18) 0%, transparent 70%)' }}
      />
      <div
        className="absolute bottom-0 right-0 w-[500px] h-[500px] pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at bottom right, rgba(91,191,160,0.08) 0%, transparent 70%)' }}
      />

      {/* Top border accent */}
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(42,143,171,0.5), rgba(91,191,160,0.35), transparent)' }} />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32">

        {/* ── Header ── */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8 mb-16">
          <div className="max-w-2xl">
            {/* Eyebrow */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border mb-5" style={{ borderColor: 'rgba(126,200,220,0.4)', background: 'rgba(126,200,220,0.12)' }}>
              <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#7EC8DC' }} />
              <span className="text-xs font-medium font-body tracking-widest uppercase" style={{ color: '#7EC8DC' }}>
                Customer Portal
              </span>
            </div>

            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.0] tracking-tight mb-4" style={{ color: '#FFFFFF' }}>
              Everything about<br />
              <span style={{ color: '#7EC8DC' }}>your shipments —</span><br />
              <span className="italic" style={{ color: 'rgba(255,255,255,0.75)' }}>in one place.</span>
            </h2>

            <p className="text-base font-body font-light leading-relaxed max-w-xl" style={{ color: 'rgba(255,255,255,0.7)' }}>
              MY WTO is your personal online dashboard — log in anytime to see exactly where your cargo is, download your documents, book new shipments, and message your WTO team. No phone calls, no waiting, no guesswork.
            </p>
          </div>

          {/* Right card — Login + Request Access toggle */}
          <div
            id="request-portal-access"
            className="shrink-0 rounded-2xl overflow-hidden lg:w-[340px] xl:w-[360px]"
            style={{ background: 'rgba(7,18,32,0.85)', border: '1px solid rgba(42,143,171,0.3)', boxShadow: '0 4px 32px rgba(0,0,0,0.3)' }}
          >
            {/* Tab toggle */}
            <div className="flex" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <button
                onClick={() => setShowForm(false)}
                className="flex-1 py-3 text-xs font-body font-semibold tracking-wider uppercase transition-all duration-200"
                style={{
                  color: !showForm ? '#7EC8DC' : '#7A9AB8',
                  background: !showForm ? 'rgba(126,200,220,0.12)' : 'transparent',
                  borderBottom: !showForm ? '2px solid #7EC8DC' : '2px solid transparent',
                }}
              >
                Log In
              </button>
              <button
                onClick={() => { setShowForm(true); setSubmitted(false); }}
                className="flex-1 py-3 text-xs font-body font-semibold tracking-wider uppercase transition-all duration-200"
                style={{
                  color: showForm ? '#5BBFA0' : '#7A9AB8',
                  background: showForm ? 'rgba(91,191,160,0.12)' : 'transparent',
                  borderBottom: showForm ? '2px solid #5BBFA0' : '2px solid transparent',
                }}
              >
                Request Access
              </button>
            </div>

            <div className="p-6">
              {!showForm ? (
                /* ── Log In view ── */
                <div className="flex flex-col gap-4">
                  <div>
                    <p className="font-semibold font-body text-base leading-snug mb-1" style={{ color: '#FFFFFF' }}>Sign in to MY WTO Portal</p>
                    <p className="text-sm font-body leading-relaxed" style={{ color: '#B8CDE0' }}>Access your dashboard, shipments, and documents instantly.</p>
                  </div>
                  {/* Access required notice */}
                  <div
                    className="flex items-start gap-2 px-3 py-2.5 rounded-lg"
                    style={{ background: 'rgba(126,200,220,0.08)', border: '1px solid rgba(126,200,220,0.2)' }}
                  >
                    <svg className="flex-shrink-0 mt-0.5" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#7EC8DC" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                    <p className="text-xs font-body leading-relaxed" style={{ color: '#A8C8D8' }}>
                      <span className="font-semibold">Registration required</span> — portal access is only available to registered WTO clients.
                    </p>
                  </div>
                  <a
                    href="https://www-w49prd.wisegrid.net/Portals/NEO/Mobile#/login"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2.5 px-6 py-3 rounded-xl text-sm font-semibold font-body transition-all duration-200 hover:opacity-90 hover:scale-[1.02]"
                    style={{ background: '#7EC8DC', color: '#0F2340' }}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M15 3h4a2 2 0 0 1 2 2m4 0a6 6 0 0 1-7.743 5.743L11 17H9v2H7v2H4a1 1 0 0 1-1-1v-2.586a1 1 0 0 1 .293-.707l5.964-5.964A6 6 0 1 1 21 9z" />
                    </svg>
                    Log In to MY WTO
                  </a>
                  <p className="text-xs font-body text-center" style={{ color: '#7A9AB8' }}>
                    Don&apos;t have credentials?{' '}
                    <button
                      onClick={() => { setShowForm(true); setSubmitted(false); }}
                      className="underline underline-offset-2 transition-colors duration-200"
                      style={{ color: '#7EC8DC' }}
                    >
                      Request access →
                    </button>
                  </p>
                </div>
              ) : submitted ? (
                /* ── Success state ── */
                <div className="flex flex-col items-center justify-center py-6 text-center gap-3">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'rgba(91,191,160,0.15)', border: '1px solid rgba(91,191,160,0.3)' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#5BBFA0" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <h4 className="font-semibold font-body text-base" style={{ color: '#FFFFFF' }}>Request Received!</h4>
                  <p className="font-body text-xs leading-relaxed" style={{ color: '#B8CDE0' }}>
                    Our team will send your MY WTO credentials within <span className="font-medium" style={{ color: '#FFFFFF' }}>1 business day</span>.
                  </p>
                  <button
                    onClick={() => { setSubmitted(false); setForm({ fullName: '', company: '', email: '', phone: '', country: '', shipmentType: '' }); }}
                    className="mt-1 text-xs font-body underline underline-offset-2 transition-colors duration-200"
                    style={{ color: '#7EC8DC' }}
                  >
                    Submit another request
                  </button>
                </div>
              ) : (
                /* ── Compact Request Access form ── */
                <form onSubmit={handleSubmit} className="flex flex-col gap-3">
                  <p className="text-xs font-body leading-relaxed mb-1" style={{ color: '#B8CDE0' }}>
                    Fill in your details and we&apos;ll set up your credentials within <span className="font-medium" style={{ color: '#FFFFFF' }}>1 business day</span>.
                  </p>

                  {/* Row 1: Full Name + Company */}
                  <div className="grid grid-cols-2 gap-2.5">
                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-body uppercase tracking-wider" style={{ fontSize: '10px', color: '#7A9AB8' }}>Full Name <span style={{ color: '#2A8FAB' }}>*</span></label>
                      <input
                        type="text"
                        name="fullName"
                        value={form.fullName}
                        onChange={handleChange}
                        required
                        placeholder="Jane Smith"
                        className="rounded-lg px-3 py-2 text-xs font-body outline-none transition-all duration-200"
                        style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: '#FFFFFF' }}
                        onFocus={(e) => { e.target.style.borderColor = 'rgba(42,143,171,0.65)'; }}
                        onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; }}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-body uppercase tracking-wider" style={{ fontSize: '10px', color: '#7A9AB8' }}>Company <span style={{ color: '#2A8FAB' }}>*</span></label>
                      <input
                        type="text"
                        name="company"
                        value={form.company}
                        onChange={handleChange}
                        required
                        placeholder="Acme Ltd."
                        className="rounded-lg px-3 py-2 text-xs font-body outline-none transition-all duration-200"
                        style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: '#FFFFFF' }}
                        onFocus={(e) => { e.target.style.borderColor = 'rgba(42,143,171,0.65)'; }}
                        onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; }}
                      />
                    </div>
                  </div>

                  {/* Row 2: Email */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-body uppercase tracking-wider" style={{ fontSize: '10px', color: '#7A9AB8' }}>Business Email <span style={{ color: '#2A8FAB' }}>*</span></label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      placeholder="jane@company.com"
                      className="rounded-lg px-3 py-2 text-xs font-body outline-none transition-all duration-200"
                      style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: '#FFFFFF' }}
                      onFocus={(e) => { e.target.style.borderColor = 'rgba(42,143,171,0.65)'; }}
                      onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; }}
                    />
                  </div>

                  {/* Row 3: Phone + Country */}
                  <div className="grid grid-cols-2 gap-2.5">
                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-body uppercase tracking-wider" style={{ fontSize: '10px', color: '#7A9AB8' }}>Phone</label>
                      <input
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        placeholder="+1 555 000"
                        className="rounded-lg px-3 py-2 text-xs font-body outline-none transition-all duration-200"
                        style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: '#FFFFFF' }}
                        onFocus={(e) => { e.target.style.borderColor = 'rgba(42,143,171,0.65)'; }}
                        onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; }}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-body uppercase tracking-wider" style={{ fontSize: '10px', color: '#7A9AB8' }}>Country <span style={{ color: '#2A8FAB' }}>*</span></label>
                      <select
                        name="country"
                        value={form.country}
                        onChange={handleChange}
                        required
                        className="rounded-lg px-3 py-2 text-xs font-body outline-none transition-all duration-200 appearance-none"
                        style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: form.country ? '#FFFFFF' : '#7A9AB8' }}
                        onFocus={(e) => { e.target.style.borderColor = 'rgba(42,143,171,0.65)'; }}
                        onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; }}
                      >
                        <option value="" disabled style={{ background: '#0E1F38' }}>Select</option>
                        <option value="bg" style={{ background: '#0E1F38' }}>Bulgaria</option>
                        <option value="de" style={{ background: '#0E1F38' }}>Germany</option>
                        <option value="nl" style={{ background: '#0E1F38' }}>Netherlands</option>
                        <option value="gb" style={{ background: '#0E1F38' }}>United Kingdom</option>
                        <option value="us" style={{ background: '#0E1F38' }}>United States</option>
                        <option value="cn" style={{ background: '#0E1F38' }}>China</option>
                        <option value="tr" style={{ background: '#0E1F38' }}>Turkey</option>
                        <option value="gr" style={{ background: '#0E1F38' }}>Greece</option>
                        <option value="ro" style={{ background: '#0E1F38' }}>Romania</option>
                        <option value="other" style={{ background: '#0E1F38' }}>Other</option>
                      </select>
                    </div>
                  </div>

                  {/* Row 4: Shipment type */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-body uppercase tracking-wider" style={{ fontSize: '10px', color: '#7A9AB8' }}>Primary Shipment Type</label>
                    <select
                      name="shipmentType"
                      value={form.shipmentType}
                      onChange={handleChange}
                      className="rounded-lg px-3 py-2 text-xs font-body outline-none transition-all duration-200 appearance-none"
                      style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: form.shipmentType ? '#FFFFFF' : '#7A9AB8' }}
                      onFocus={(e) => { e.target.style.borderColor = 'rgba(42,143,171,0.65)'; }}
                      onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; }}
                    >
                      <option value="" style={{ background: '#0E1F38' }}>Select type (optional)</option>
                      <option value="sea-fcl" style={{ background: '#0E1F38' }}>Sea Freight — FCL</option>
                      <option value="sea-lcl" style={{ background: '#0E1F38' }}>Sea Freight — LCL</option>
                      <option value="air" style={{ background: '#0E1F38' }}>Air Freight</option>
                      <option value="inland" style={{ background: '#0E1F38' }}>Inland / Road</option>
                      <option value="customs" style={{ background: '#0E1F38' }}>Customs Brokerage</option>
                      <option value="multimodal" style={{ background: '#0E1F38' }}>Multimodal</option>
                    </select>
                  </div>

                  {error && (
                    <div className="px-3 py-2 rounded-lg" style={{ background: 'rgba(220,38,38,0.12)', border: '1px solid rgba(220,38,38,0.3)' }}>
                      <p className="text-xs font-body" style={{ color: '#FCA5A5' }}>{error}</p>
                    </div>
                  )}

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="mt-1 inline-flex items-center justify-center gap-2 w-full py-3 rounded-xl text-sm font-semibold font-body transition-all duration-200 hover:opacity-90 hover:scale-[1.01] disabled:opacity-60 disabled:cursor-not-allowed"
                    style={{ background: '#7EC8DC', color: '#0F2340', boxShadow: '0 4px 16px rgba(126,200,220,0.3)' }}
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                        </svg>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M15 7a2 2 0 0 1 2 2m4 0a6 6 0 0 1-7.743 5.743L11 17H9v2H7v2H4a1 1 0 0 1-1-1v-2.586a1 1 0 0 1 .293-.707l5.964-5.964A6 6 0 1 1 21 9z" />
                        </svg>
                        Request MY WTO Access
                      </>
                    )}
                  </button>

                  <p className="text-center font-body leading-relaxed" style={{ fontSize: '10px', color: '#7A9AB8' }}>
                    Approved within 1 business day · Secure &amp; Encrypted
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* ── Laptop/Browser Frame Mock Dashboard ── */}
        <div className="mb-16" ref={portalStatsRef}>
          {/* Browser/Laptop Frame */}
          <div className="relative max-w-4xl mx-auto">
            <div className="absolute inset-0 -z-10 rounded-2xl" style={{ background: 'radial-gradient(ellipse at center, rgba(42,143,171,0.15) 0%, transparent 70%)', filter: 'blur(30px)', transform: 'scale(1.1)' }} />

            <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(42,143,171,0.3)', background: '#071220', boxShadow: '0 32px 80px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.04)' }}>
              {/* Browser chrome bar */}
              <div className="flex items-center gap-3 px-4 py-3" style={{ background: '#0A1C2E', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full" style={{ background: '#FF5F57' }} />
                  <div className="w-3 h-3 rounded-full" style={{ background: '#FFBD2E' }} />
                  <div className="w-3 h-3 rounded-full" style={{ background: '#28C840' }} />
                </div>
                <div className="flex-1 flex items-center gap-2 rounded-md px-3 py-1.5 mx-4" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                  <span className="text-xs font-body" style={{ color: 'rgba(255,255,255,0.35)', fontSize: '10px' }}>portal.wto.com/dashboard</span>
                </div>
                <div className="flex items-center gap-2">
                  {[0, 1, 2].map(i => (
                    <div key={i} className="w-6 h-6 rounded flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.04)' }}>
                      <div className="w-3 h-0.5 rounded" style={{ background: 'rgba(255,255,255,0.2)' }} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Dashboard content */}
              <div className="flex" style={{ minHeight: '340px' }}>
                <div className="w-48 shrink-0 flex flex-col gap-1 p-3" style={{ background: '#050E1A', borderRight: '1px solid rgba(255,255,255,0.05)' }}>
                  <div className="flex items-center gap-2 px-2 py-3 mb-2">
                    <div className="w-6 h-6 rounded" style={{ background: '#1B6B8A' }} />
                    <span className="text-xs font-bold font-body text-white">MY WTO</span>
                  </div>
                  {[
                    { label: 'Dashboard', active: true, icon: 'M3 12l2-2m0 0l7-7 7 7M5 21h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
                    { label: 'Shipments', active: false, icon: 'M9 20l-5.447-2.724A1 1 0 0 1 3 16.382V5.618a1 1 0 0 1 1.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7' },
                    { label: 'Documents', active: false, icon: 'M9 12h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z' },
                    { label: 'Bookings', active: false, icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
                    { label: 'Quotes', active: false, icon: 'M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z' },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg"
                      style={{
                        background: item.active ? 'rgba(27,107,138,0.18)' : 'transparent',
                        borderLeft: item.active ? '2px solid #1B6B8A' : '2px solid transparent',
                      }}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={item.active ? '#2A8FAB' : 'rgba(255,255,255,0.3)'} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                        <path d={item.icon} />
                      </svg>
                      <span className="text-xs font-body" style={{ color: item.active ? '#2A8FAB' : 'rgba(255,255,255,0.4)', fontSize: '11px' }}>{item.label}</span>
                    </div>
                  ))}
                </div>

                <div className="flex-1 p-4 flex flex-col gap-3 overflow-hidden">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-semibold font-body text-sm">Good morning, Acme Ltd.</p>
                      <p className="text-white/35 font-body" style={{ fontSize: '10px' }}>Last login: Today, 08:42 AM</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#5BBFA0' }} />
                      <span className="text-xs font-body" style={{ color: '#5BBFA0', fontSize: '10px' }}>Live</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { label: 'Active Shipments', value: '12', color: '#2A8FAB' },
                      { label: 'Pending Docs', value: '3', color: '#FFBD2E' },
                      { label: 'Delivered (30d)', value: '47', color: '#5BBFA0' },
                    ].map((card) => (
                      <div key={card.label} className="rounded-lg p-2.5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                        <p className="font-display text-xl font-bold" style={{ color: card.color }}>{card.value}</p>
                        <p className="text-white/35 font-body" style={{ fontSize: '9px' }}>{card.label}</p>
                      </div>
                    ))}
                  </div>

                  <div className="rounded-lg overflow-hidden flex-1" style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
                    <div className="grid grid-cols-4 px-3 py-2" style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                      {['Shipment ID', 'Route', 'Status', 'ETA'].map(h => (
                        <span key={h} className="text-white/30 font-body font-semibold uppercase" style={{ fontSize: '8px', letterSpacing: '0.08em' }}>{h}</span>
                      ))}
                    </div>
                    {[
                      { id: 'WTO-2024-8821', route: 'Shanghai → Hamburg', status: 'In Transit', statusColor: '#2A8FAB', eta: 'Apr 14' },
                      { id: 'WTO-2024-8819', route: 'Sofia → New York', status: 'Customs', statusColor: '#FFBD2E', eta: 'Apr 10' },
                      { id: 'WTO-2024-8815', route: 'Hanoi → Rotterdam', status: 'Delivered', statusColor: '#5BBFA0', eta: 'Apr 06' },
                      { id: 'WTO-2024-8810', route: 'Berlin → Taipei', status: 'Booked', statusColor: 'rgba(255,255,255,0.4)', eta: 'Apr 22' },
                    ].map((row, i) => (
                      <div key={row.id} className="grid grid-cols-4 px-3 py-2 items-center" style={{ borderBottom: i < 3 ? '1px solid rgba(255,255,255,0.04)' : 'none', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                        <span className="font-body font-medium" style={{ color: '#2A8FAB', fontSize: '9px' }}>{row.id}</span>
                        <span className="text-white/50 font-body" style={{ fontSize: '9px' }}>{row.route}</span>
                        <span className="inline-flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full" style={{ background: row.statusColor }} />
                          <span className="font-body" style={{ color: row.statusColor, fontSize: '9px' }}>{row.status}</span>
                        </span>
                        <span className="text-white/40 font-body" style={{ fontSize: '9px' }}>{row.eta}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="relative mx-auto mt-0" style={{ width: '70%', height: '12px', background: 'linear-gradient(180deg, #0A1C2E 0%, #050E1A 100%)', borderRadius: '0 0 8px 8px', borderTop: '1px solid rgba(255,255,255,0.04)' }} />
            <div className="relative mx-auto" style={{ width: '40%', height: '6px', background: '#050E1A', borderRadius: '0 0 12px 12px', boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }} />
          </div>

          <p className="text-center font-body mt-4" style={{ fontSize: '11px', color: '#7A9AB8' }}>
            Preview of MY WTO Portal dashboard — <span style={{ color: '#A8C8D8', fontWeight: 600 }}>registration required</span> to access · available to all WTO clients
          </p>
        </div>

        {/* ── Benefits grid ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {benefits.map((b, i) => (
            <div
              key={i}
              className="group rounded-2xl p-5 transition-all duration-300 hover:scale-[1.01]"
              style={{
                background: 'rgba(126,200,220,0.18)',
                border: '1px solid rgba(126,200,220,0.45)',
                boxShadow: '0 2px 12px rgba(42,143,171,0.12)',
              }}
            >
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center mb-3"
                style={{ background: 'rgba(126,200,220,0.35)', border: '1px solid rgba(126,200,220,0.6)' }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0F2340" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <path d={b.icon} />
                </svg>
              </div>
              <h3 className="font-semibold font-body text-sm mb-1.5 leading-snug" style={{ color: '#FFFFFF' }}>{b.title}</h3>
              <p className="text-xs font-body leading-relaxed" style={{ color: '#D6EEF5' }}>{b.desc}</p>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default MyWTOPortalSection;
